import { Community } from "@entities";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { CircularProgress, Stack } from "@mui/material";
import { communityAssociationRepo, communityRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { communityJoinUrl } from "@shared/helpers/constants";
import {
  countGroupedById,
  useDisplayedIdCounts,
} from "../../../helpers/admin-list-query";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CommunityCard from "../../../components/popups/community-card/CommunityCard";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { buildCommunityColumns } from "./columns";
import { COMMUNITY_TABLE_FILTERS } from "./filters";
import "./CommunitiesTable.scss";

const DELETE_BLOCKED_MESSAGE =
  "לא ניתן למחוק קהילה עם מתנדבים או קהילות בנות. יש להסיר אותם קודם.";

const CommunitiesTable: FC = () => {
  const { HyperTable } = useCreateHyperTable(Community);
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const question = useQuestion();
  const navigate = useNavigate();
  const { communityId } = useParams<"communityId">();
  const [selectedCommunity, setSelectedCommunity] = useState<Community>();
  const [isAddCommunity, setIsAddCommunity] = useState(false);
  const loadVolunteerCounts = useCallback(
    (ids: string[]) =>
      countGroupedById(communityAssociationRepo, "communityId", ids),
    [],
  );
  const { counts: volunteerCountById, onDisplayedDataChange } =
    useDisplayedIdCounts(loadVolunteerCounts);

  const copyRegistrationLink = useCallback(
    (id: string) => {
      navigator.clipboard.writeText(communityJoinUrl(id));
      setAlertText("קישור ההרשמה הועתק ללוח");
      setSeverity("success");
    },
    [setAlertText, setSeverity],
  );

  const openEdit = useCallback((community: Community) => {
    setSelectedCommunity(community);
    setIsAddCommunity(false);
  }, []);

  const closeEdit = useCallback(() => {
    setSelectedCommunity(undefined);
    setIsAddCommunity(false);
  }, []);

  useEffect(() => {
    if (!communityId || !currOrganization?.id) return;

    void communityRepo
      .findOne({
        where: {
          id: communityId,
          organizationId: currOrganization.id,
        },
        include: {
          parentCommunity: true,
          associations: true,
        },
      })
      .then((community) => {
        if (community) openEdit(community);
      });
  }, [communityId, currOrganization?.id, openEdit]);

  const deleteCommunities = useCallback(
    async (
      where: Parameters<typeof communityRepo.deleteMany>[0]["where"],
      successMessage = "הקהילות נמחקו בהצלחה",
    ) => {
      try {
        await communityRepo.deleteMany({ where });
        setAlertText(successMessage);
        setSeverity("success");
        return true;
      } catch {
        setAlertText(DELETE_BLOCKED_MESSAGE);
        setSeverity("error");
        return false;
      }
    },
    [setAlertText, setSeverity],
  );

  const getMenuActions = useCallback<SingleRowActionGetter<Community>>(
    () => [
      {
        text: "מסך שליטה",
        infoTooltip: "סטטיסטיקות חברים והתנדבויות",
        icon: <DashboardIcon />,
        onClick: (_e, row) => {
          navigate(`/communities/${row.original.id}/dashboard`);
          return { refetch: false };
        },
      },
      {
        text: "קישור הרשמה לקהילה",
        infoTooltip: "העתקת קישור הרשמה למתנדבים",
        icon: <ContentCopyIcon />,
        onClick: (_e, row) => {
          copyRegistrationLink(row.original.id);
          return { refetch: false };
        },
      },
      {
        text: "עריכה",
        icon: <EditIcon />,
        onClick: (_, row) => {
          openEdit(row.original);
          return { refetch: false };
        },
      },
      {
        text: "מחיקה",
        icon: <DeleteOutlineIcon />,
        onClick: async (_, row) => {
          const yes = await question(
            `האם למחוק את הקהילה "${row.original.name}"?`,
          );
          if (!yes) return { refetch: false };

          const deleted = await deleteCommunities(
            { id: row.original.id },
            "הקהילה נמחקה בהצלחה",
          );
          return { refetch: deleted };
        },
      },
    ],
    [copyRegistrationLink, deleteCommunities, navigate, openEdit, question],
  );

  const buttons = useMemo<ActionButton<Community>[]>(
    () => [
      {
        icon: <DeleteOutlineIcon />,
        title: "מחיקה",
        onClick: async ({ whereToUpdate }) => {
          const yes = await question("האם למחוק את הקהילות שנבחרו?");
          if (!yes) return { refetch: false };

          const deleted = await deleteCommunities(whereToUpdate);
          return { refetch: deleted, resetSelection: deleted };
        },
      },
    ],
    [deleteCommunities, question],
  );

  if (!currOrganization) {
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <div className="communities-table">
      <HyperTable
        tableId="communities-table"
        components={{
          addRowButton: {
            onClick: () => {
              setIsAddCommunity(true);
              setSelectedCommunity(undefined);
            },
            content: "הוספת קהילה",
          },
        }}
        findConfig={{
          initialOrderBy: {
            createdAt: "desc",
            name: "asc",
          },
          where: Community.hyperFilter({
            organization: { $id: currOrganization.id },
          }),
          enabled: !!currOrganization.id,
          include: {
            parentCommunity: true,
          },
        }}
        onDisplayedDataChange={onDisplayedDataChange}
        onRowClick={(_, row) => openEdit(row.original)}
        title={<TableHeaderTitles titles={[{ content: "קהילות" }]} />}
      >
        <HyperTable.Toolbar>
          {COMMUNITY_TABLE_FILTERS.map((filter) =>
            isEntityFilter(filter) ? (
              <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
            ) : (
              <HyperTable.CustomFilter key={filter.id} {...filter} />
            ),
          )}
        </HyperTable.Toolbar>
        <HyperTable.SelectColumn />
        <HyperTable.MenuColumn actions={getMenuActions} />
        {buildCommunityColumns(volunteerCountById).map((column) => (
          <HyperTable.Column key={column.field} {...column} />
        ))}
        {(!!selectedCommunity || isAddCommunity) && (
          <CommunityCard
            isEdit={!isAddCommunity}
            isOpen
            communityDetails={selectedCommunity}
            onComplete={() => {
              HyperTable.context.current?.refetch("remove");
            }}
            onClose={closeEdit}
          />
        )}
        <HyperTable.ActionsBar buttons={buttons} />
      </HyperTable>
    </div>
  );
};

export default CommunitiesTable;
